import React from 'react';
import { motion } from 'motion/react';
import { Link, useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import LandingHeader from '../components/LandingHeader';
import Footer from '../components/Footer';
import { useAuth } from '../contexts/AuthContext';

import {
  ArrowRight,
  CheckCircle,
  ClipboardText,
  FileText,
  FileArrowDown,
  Lightning,
  Clock,
  Shield,
  Upload,
  ChartBar,
  PencilSimpleLine,
  Eye,
  Question
} from '@phosphor-icons/react';

const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_69a5bbf3-0908-44b9-a2e8-23182887b620/artifacts/ya4hxr5p_Teacher-marking-img1.png";
const CTA_LINK = "/marketplace/assignment-marker-with-rubric";

const springConfig = {
  type: 'spring',
  damping: 80,
  stiffness: 400,
  mass: 1,
};

const WORKER_SLUG = 'assignment-marker-with-rubric';

const AssignmentMarkerLanding = () => {
  const navigate = useNavigate();
  const { user, authStatus } = useAuth();
  const isAuthenticated = authStatus === 'authenticated' && user;

  const handleCTA = () => {
    if (isAuthenticated) {
      navigate(CTA_LINK);
    } else {
      localStorage.setItem('install_intent', JSON.stringify({ slug: WORKER_SLUG, timestamp: Date.now() }));
      localStorage.setItem('post_auth_redirect', `/marketplace/${WORKER_SLUG}?auto_install=true`);
      navigate(`/login?redirect=${encodeURIComponent(`/marketplace/${WORKER_SLUG}?auto_install=true`)}`);
    }
  };

  return (
    <div className="bg-[#161B26] min-h-screen" data-testid="assignment-marker-landing">
      <SEO
        title="Mark 30 Student Assignments in Under 10 Minutes | Man & Machine"
        description="Upload student work and get structured grades, feedback, and comments instantly. Works with your rubric. Free to try."
        canonicalUrl="https://manmachine.agency/assignment-marker"
      />
      <LandingHeader />

      {/* ══════════════════════════════════════════════════════════════════════
          HERO SECTION
          ══════════════════════════════════════════════════════════════════════ */}
      <section className="pt-28 pb-20 relative overflow-hidden min-h-[80vh] flex items-center" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGE}
            alt=""
            className="w-full h-full object-cover"
            style={{ opacity: 0.45, filter: 'saturate(0.7)', objectPosition: '65% center' }}
          />
          <div className="absolute inset-0" style={{
            background: `
              linear-gradient(to right, #161B26 0%, rgba(22,27,38,0.9) 40%, rgba(22,27,38,0.5) 65%, rgba(22,27,38,0.25) 100%),
              linear-gradient(to bottom, #161B26 0%, transparent 15%, transparent 80%, #161B26 100%)
            `
          }} />
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-6">
          <div className="max-w-xl mr-auto ml-[5%]">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={springConfig}
              className="text-left"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[rgba(0,229,192,0.08)] border border-[rgba(0,229,192,0.2)] mb-6">
                <PencilSimpleLine size={14} className="text-[#00E5C0]" />
                <span className="text-xs text-[#A0AEC0]">For Teachers and Educators</span>
              </div>

              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-[1.12] mb-5">
                Mark 30 Assignments{' '}
                <span className="text-[#00E5C0]">in Under 10 Minutes.</span>
              </h1>

              <p className="text-lg text-[#A0AEC0] mb-3 leading-relaxed">
                Upload student work and get structured grades, feedback, and comments instantly.
              </p>

              <p className="text-sm text-[#7D8896] mb-10">
                No setup required. Works with your rubric. Free to try.
              </p>

              <div className="flex flex-wrap gap-4 mb-4">
                <button
                  onClick={handleCTA}
                  className="inline-flex items-center gap-2 bg-[#00E5C0] hover:bg-[#00CCA8] text-[#0A0F14] font-semibold text-lg px-10 py-4 rounded-xl transition-colors shadow-[0_4px_20px_rgba(0,229,192,0.25)]"
                  data-testid="hero-cta-button"
                >
                  Try it with one assignment
                  <ArrowRight size={18} />
                </button>
                <a
                  href="#how-it-works"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] text-white font-semibold text-lg rounded-xl hover:bg-[rgba(255,255,255,0.08)] transition-all"
                  data-testid="hero-secondary-cta"
                >
                  See How It Works
                </a>
              </div>

              <div className="flex flex-wrap gap-x-5 gap-y-2 mt-10 mb-4">
                {[
                  "Structured grades and feedback",
                  "Works with your rubric",
                  "Review before finalising"
                ].map((point, idx) => (
                  <span key={idx} className="flex items-center gap-2 text-sm text-[#8A919E]">
                    <CheckCircle size={14} className="text-[#10B981]/75" />
                    {point}
                  </span>
                ))}
              </div>

              <div className="flex items-center gap-4 text-sm mt-2">
                <span className="text-[#5a5f72]">Already have an account?</span>
                <Link
                  to="/login"
                  className="text-[#00E5C0] hover:text-[#33EDCF] font-medium transition-colors flex items-center gap-1"
                >
                  Sign in
                  <ArrowRight size={12} />
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          HOW IT WORKS (3 Steps)
          ══════════════════════════════════════════════════════════════════════ */}
      <section id="how-it-works" className="py-24 bg-[rgba(255,255,255,0.02)]" data-testid="how-it-works-section">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Three Steps.{' '}
              <span className="text-[#00E5C0]">That's It.</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                number: "1",
                icon: Upload,
                title: "Upload student work",
                description: "Drag and drop essays, reports, or short answers. Supports PDF, Word, and text files.",
                color: "#00E5C0"
              },
              {
                number: "2",
                icon: ClipboardText,
                title: "Add your rubric (optional)",
                description: "Paste your grading criteria or let the system apply general academic standards.",
                color: "#10B981"
              },
              {
                number: "3",
                icon: FileArrowDown,
                title: "Get instant grades and feedback",
                description: "Receive structured grades, written comments, and a clear breakdown per criteria.",
                color: "#F59E0B"
              }
            ].map((step, idx) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ ...springConfig, delay: idx * 0.1 }}
                data-testid={`step-${step.number}-card`}
              >
                <div className="glass rounded-2xl p-6 h-full border border-[rgba(255,255,255,0.05)] hover:border-[rgba(0,229,192,0.3)] transition-colors">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                    style={{ backgroundColor: `${step.color}20` }}
                  >
                    <step.icon size={24} style={{ color: step.color }} />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{step.title}</h3>
                  <p className="text-sm text-[#A0AEC0] leading-relaxed">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          OUTPUT PREVIEW (Most important section)
          ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-24" data-testid="output-preview-section">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              See What You{' '}
              <span className="text-[#00E5C0]">Get Back</span>
            </h2>
            <p className="text-base text-[#A0AEC0]">
              A structured, clear report for every piece of student work.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ ...springConfig, delay: 0.15 }}
          >
            <OutputPreviewCard />
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          VALUE SECTION
          ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-24 bg-[rgba(255,255,255,0.02)]" data-testid="value-section">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Give Hours Back{' '}
              <span className="text-[#00E5C0]">to Your Week</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 items-start">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={springConfig}
            >
              <div className="grid gap-4">
                {[
                  { icon: Lightning, text: "Mark faster without sacrificing quality" },
                  { icon: ChartBar, text: "Ensure consistent grading across all students" },
                  { icon: Clock, text: "Reduce burnout and spend more time teaching" },
                  { icon: Eye, text: "Always review and edit before finalising" }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-4 glass rounded-xl" data-testid="value-bullet">
                    <item.icon size={20} className="text-[#00E5C0] flex-shrink-0" />
                    <p className="text-[#E2E8F0] font-medium">{item.text}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ ...springConfig, delay: 0.1 }}
            >
              <TimeComparisonCard />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          TRUST / SAFETY SECTION
          ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-20" data-testid="trust-section">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              You Stay in <span className="text-[#00E5C0]">Control</span>
            </h2>
            <p className="text-base text-[#A0AEC0] mb-10 leading-relaxed">
              Designed to assist teachers, not replace them. Your marking criteria, your final say.
            </p>

            <div className="grid sm:grid-cols-3 gap-4">
              {[
                { icon: Eye, text: "Review before using results" },
                { icon: ClipboardText, text: "Works with your criteria" },
                { icon: Shield, text: "You make the final call" }
              ].map((item, idx) => (
                <div key={idx} className="flex items-center justify-center gap-2 p-4 glass rounded-xl" data-testid="trust-pill">
                  <item.icon size={18} className="text-[#00E5C0] flex-shrink-0" />
                  <span className="text-[#E2E8F0] text-sm font-medium">{item.text}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          FAQ SECTION
          ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-20 bg-[rgba(255,255,255,0.02)]" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white">
              Common <span className="text-[#00E5C0]">Questions</span>
            </h2>
          </motion.div>

          <div className="grid gap-4">
            {[
              {
                q: "Will this replace my professional judgement?",
                a: "No. You always review, edit, and approve every grade and comment before it reaches a student. Think of it as a first draft you refine."
              },
              {
                q: "What file types does it support?",
                a: "PDF, Word documents, plain text, and image files. Upload whatever your students submit."
              },
              {
                q: "Do I need to provide a rubric?",
                a: "It's optional. You can paste your own grading criteria for precise marking, or let the system apply general academic standards."
              },
              {
                q: "How accurate is the grading?",
                a: "It provides a strong starting point. Most teachers find they adjust scores by 5-10% on average. The real time saver is the structured feedback and comments."
              },
              {
                q: "Is student data secure?",
                a: "Yes. Files are processed and not stored permanently. Your data stays yours."
              }
            ].map((faq, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ ...springConfig, delay: idx * 0.08 }}
                className="glass rounded-xl p-6 border border-[rgba(255,255,255,0.05)]"
              >
                <div className="flex items-start gap-3">
                  <Question size={20} className="text-[#00E5C0] flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-white font-semibold mb-2">{faq.q}</h3>
                    <p className="text-[#A0AEC0] text-sm">{faq.a}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          FINAL CTA SECTION
          ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-24" data-testid="final-cta-section">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springConfig}
          >
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              Ready to Reclaim{' '}
              <span className="text-[#00E5C0]">Your Evenings?</span>
            </h2>

            <p className="text-lg text-[#A0AEC0] mb-10">
              Takes less than 60 seconds to get started.
            </p>

            <div className="flex flex-wrap justify-center gap-4 mb-4">
              <button
                onClick={handleCTA}
                className="inline-flex items-center gap-2 px-8 py-4 bg-[#00E5C0] hover:bg-[#00CCA8] text-[#0A0F14] text-lg font-semibold rounded-xl transition-colors shadow-[0_4px_20px_rgba(0,229,192,0.25)]"
                data-testid="final-cta-button"
              >
                Try it with one assignment
                <ArrowRight size={20} />
              </button>
            </div>

            <p className="text-sm text-[#718096]">
              No credit card required. Free to try.
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   OUTPUT PREVIEW CARD
   ═══════════════════════════════════════════════════════════ */

const OutputPreviewCard = () => (
  <div className="glass rounded-2xl border border-[rgba(0,229,192,0.15)] overflow-hidden" data-testid="output-preview-card">
    {/* Header */}
    <div className="px-8 pt-8 pb-6 border-b border-[rgba(255,255,255,0.06)]">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-[#7D8896] mb-1 uppercase tracking-wide">Student Assignment</p>
          <p className="text-base font-semibold text-white">
            Essay: The Impact of Climate Change on Agriculture
          </p>
        </div>
        <div className="text-right">
          <p className="text-3xl font-bold text-[#00E5C0]">B+</p>
          <p className="text-sm text-[#A0AEC0]">78%</p>
        </div>
      </div>
    </div>

    {/* Criteria */}
    <div className="px-8 py-6 border-b border-[rgba(255,255,255,0.06)]">
      <p className="text-xs font-medium text-[#7D8896] uppercase tracking-wider mb-4">Criteria Breakdown</p>
      <div className="space-y-3">
        <CriteriaRow name="Content" score={8} max={10} />
        <CriteriaRow name="Structure" score={7} max={10} />
        <CriteriaRow name="Grammar" score={9} max={10} />
      </div>
    </div>

    {/* Feedback */}
    <div className="px-8 py-6 border-b border-[rgba(255,255,255,0.06)]">
      <p className="text-xs font-medium text-[#7D8896] uppercase tracking-wider mb-3">Feedback</p>
      <p className="text-sm text-[#A0AEC0] leading-relaxed">
        Strong arguments presented in the second paragraph, but the conclusion lacks a definitive summary. Good use of vocabulary throughout.
      </p>
    </div>

    {/* Strengths + Improvements */}
    <div className="px-8 py-6 grid grid-cols-1 sm:grid-cols-2 gap-6">
      <div>
        <p className="text-xs font-medium text-[#7D8896] uppercase tracking-wider mb-3">Strengths</p>
        <div className="space-y-2">
          <FeedbackItem text="Clear thesis statement" positive />
          <FeedbackItem text="Good use of vocabulary" positive />
        </div>
      </div>
      <div>
        <p className="text-xs font-medium text-[#7D8896] uppercase tracking-wider mb-3">Areas for improvement</p>
        <div className="space-y-2">
          <FeedbackItem text="Conclusion needs strengthening" />
          <FeedbackItem text="Add more cited sources" />
        </div>
      </div>
    </div>
  </div>
);

const CriteriaRow = ({ name, score, max }) => {
  const pct = (score / max) * 100;
  return (
    <div className="flex items-center gap-4">
      <span className="text-sm text-[#A0AEC0] w-24">{name}</span>
      <div className="flex-1 h-1.5 bg-[rgba(255,255,255,0.06)] rounded-full overflow-hidden">
        <div
          className="h-full rounded-full bg-[#00E5C0]"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-sm text-[#A0AEC0] tabular-nums w-12 text-right">{score}/{max}</span>
    </div>
  );
};

const FeedbackItem = ({ text, positive }) => (
  <div className="flex items-start gap-2">
    <div className={`mt-0.5 w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${
      positive ? 'bg-[rgba(16,185,129,0.15)]' : 'bg-[rgba(245,158,11,0.15)]'
    }`}>
      {positive
        ? <CheckCircle size={10} className="text-[#10B981]" />
        : <ArrowRight size={10} className="text-[#F59E0B]" />
      }
    </div>
    <span className="text-sm text-[#A0AEC0]">{text}</span>
  </div>
);

const TimeComparisonCard = () => (
  <div className="glass rounded-2xl p-8 border border-[rgba(255,255,255,0.05)]" data-testid="time-comparison-card">
    <div className="flex items-center gap-2 mb-6">
      <Clock size={18} className="text-[#00E5C0]" />
      <span className="text-sm text-[#A0AEC0] font-semibold">Time to mark 30 assignments</span>
    </div>
    <div className="space-y-5">
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-[#A0AEC0]">Manual marking</span>
          <span className="text-sm font-medium text-[#7D8896]">~5 hours</span>
        </div>
        <div className="h-2.5 bg-[rgba(255,255,255,0.06)] rounded-full overflow-hidden">
          <div className="h-full rounded-full bg-[rgba(255,255,255,0.12)]" style={{ width: '100%' }} />
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-[#A0AEC0]">With Assignment Marker</span>
          <span className="text-sm font-medium text-[#00E5C0]">~10 min</span>
        </div>
        <div className="h-2.5 bg-[rgba(255,255,255,0.06)] rounded-full overflow-hidden">
          <div className="h-full rounded-full bg-[#00E5C0]" style={{ width: '3.3%' }} />
        </div>
      </div>
    </div>
    <p className="text-xs text-[#7D8896] mt-6">Based on a standard class of 30 students</p>
  </div>
);

export default AssignmentMarkerLanding;
